import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_NAME } from "@/lib/site";

export const Seo = ({ title, description, path = "/", image, type = "website", jsonLd, noindex = false }) => {
  const fullTitle = title ? `${title} | ${SITE_NAME} — Whitstable` : `${SITE_NAME} | Dance & Performing Arts School in Whitstable, Kent`;
  const desc =
    description ||
    "Kerry's is a fun, inclusive dance and performing arts school in Whitstable, Kent. ISTD Ballet, Tap, Modern, Musical Theatre, Street Dance and Audition Preparation for ages 2–18, with a 100% ISTD exam pass record, private lessons and costume hire. SEND and neurodiverse dancers warmly welcome.";
  const url = `${SITE_URL}${path}`;
  const img = image || "https://images.unsplash.com/photo-1629219222026-e48dcc036c3f?w=1200&q=80";
  return (
    <Helmet>
      <html lang="en-GB" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex ? <meta name="robots" content="noindex, follow" /> : <meta name="robots" content="index, follow" />}
      <link rel="canonical" href={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_GB" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

// Dance styles taught — used for the Course structured data (AEO / rich results)
const DANCE_STYLES = [
  { name: "Ballet", desc: "Classical ballet training following the ISTD syllabus, ages 2 to 18." },
  { name: "Tap", desc: "ISTD tap building musicality, rhythm and sharp footwork, ages 4 to 18." },
  { name: "Modern", desc: "Energetic ISTD modern theatre dance — kicks, leaps and turns, ages 6 to 18." },
  { name: "Musical Theatre", desc: "Triple-threat singing, dancing and acting for ages 4 to 18." },
  { name: "Street Dance", desc: "Commercial and hip hop styles for ages 4 to 18." },
  { name: "Contemporary", desc: "Fluid, expressive contemporary technique for ages 11 to 18." },
  { name: "Audition Preparation", desc: "Coaching for vocational college and professional auditions, ages 11+." },
];

export const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["DanceSchool", "EducationalOrganization", "LocalBusiness"],
  "@id": `${SITE_URL}/#organization`,
  name: "Kerry's",
  alternateName: "Newcomb Theatre Arts",
  description:
    "Fun, inclusive dance and performing arts school in Whitstable, Kent. ISTD Ballet, Tap, Modern, Musical Theatre, Street Dance, Contemporary and Audition Preparation for ages 2–18. 100% ISTD exam pass record. SEND and neurodiverse dancers warmly welcome.",
  url: SITE_URL,
  logo: `${SITE_URL}/brand/kerrys-logo.png`,
  image: `${SITE_URL}/brand/kerrys-logo-square.png`,
  telephone: "+447904530364",
  email: "info@kerrys.dance",
  address: {
    "@type": "PostalAddress",
    streetAddress: "76 Castle Road",
    addressLocality: "Whitstable",
    addressRegion: "Kent",
    postalCode: "CT5 2EB",
    addressCountry: "GB",
  },
  geo: { "@type": "GeoCoordinates", latitude: 51.3603, longitude: 1.0257 },
  areaServed: ["Whitstable", "Canterbury", "Herne Bay", "Tankerton", "Chestfield", "Kent"],
  knowsAbout: ["Ballet", "Tap", "Modern dance", "Musical theatre", "Street dance", "Contemporary dance", "ISTD examinations", "Children's dance", "SEND dance"],
  founder: { "@type": "Person", name: "Kerry White" },
  sameAs: ["https://www.facebook.com/kerrys.dance", "https://www.instagram.com/kerrys.dance"],
  priceRange: "££",
  location: [
    { "@type": "Place", name: "The Umbrella Centre", address: { "@type": "PostalAddress", streetAddress: "St Mary's Hall, Oxford Street", addressLocality: "Whitstable", addressRegion: "Kent", postalCode: "CT5 1DD", addressCountry: "GB" } },
    { "@type": "Place", name: "The Whitstable School – Dance Studio", address: { "@type": "PostalAddress", streetAddress: "Bellevue Road", addressLocality: "Whitstable", addressRegion: "Kent", postalCode: "CT5 1PX", addressCountry: "GB" } },
    { "@type": "Place", name: "Whitstable Swimming Pool – Dance Studio", address: { "@type": "PostalAddress", streetAddress: "Tower Parade", addressLocality: "Whitstable", addressRegion: "Kent", postalCode: "CT5 2BJ", addressCountry: "GB" } },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Dance classes and services",
    itemListElement: [
      ...DANCE_STYLES.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Course", name: `${s.name} classes`, description: s.desc } })),
      { "@type": "Offer", name: "Private dance lessons", price: "35", priceCurrency: "GBP", description: "One-to-one private dance lessons, all ages and styles, £35 per hour including a local Whitstable venue." },
      { "@type": "Offer", name: "Children's musical theatre costume hire", description: "Musical theatre costumes for children aged 2–18, carefully stored in Whitstable." },
    ],
  },
};

// Website node with search action (helps engines understand site structure)
export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-GB",
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/blog?search={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

// Build a FAQPage schema from [{q, a}] — powers "People also ask" / AEO answers
// Build Review + AggregateRating structured data from admin-managed reviews.
// Attached to the organisation. (Note: Google shows review stars in search from your
// Google Business Profile — self-hosted markup is still valid for other engines/AEO.)
export const buildReviewsJsonLd = (reviews = []) => {
  const valid = (reviews || []).filter((r) => r && r.rating);
  if (valid.length === 0) return null;
  const avg = valid.reduce((s, r) => s + Number(r.rating || 0), 0) / valid.length;
  return {
    "@context": "https://schema.org",
    "@type": "DanceSchool",
    "@id": `${SITE_URL}/#organization`,
    name: "Kerry's",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: Math.round(avg * 10) / 10,
      reviewCount: valid.length,
      bestRating: 5,
      worstRating: 1,
    },
    review: valid.slice(0, 20).map((r) => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      author: { "@type": "Person", name: r.author_name },
      reviewBody: r.text,
    })),
  };
};

export const buildFaqJsonLd = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// ItemList of Courses for the Classes page
export const CLASSES_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Dance classes at Kerry's, Whitstable",
  itemListElement: DANCE_STYLES.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Course",
      name: `${s.name} classes in Whitstable`,
      description: s.desc,
      provider: { "@id": `${SITE_URL}/#organization` },
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "onsite",
        location: { "@type": "Place", name: "Whitstable, Kent" },
      },
    },
  })),
};
